import { useState, MouseEvent, useEffect, useRef, useContext } from 'react';
import {
  RiveFile,
  useRive,
  useStateMachineInput,
} from '@rive-app/react-webgl2';
import { CSSTransition } from 'react-transition-group';
import useSWR from 'swr';
import styles from './ReactionButton.module.css';
import ReactionModal from './ReactionModal';
import { insertIf, join } from 'lib/helpers';
import { jsonFetcherWithHandler as fetcher } from 'lib/swr';
import { CommunityShotItem, CommunityReactions } from 'types/community';
import {
  updateCommunityReaction,
  removeCommunityReaction,
} from 'lib/api/community_api';
import { UserContext } from 'contexts/user';
import { useRouter } from 'next/router';
import * as analytics from 'lib/analytics';
import { VoidCallback } from 'types/callbacks';
import { RiveFilesContext } from 'contexts/riveFile/RiveFilesContext';

function reactionsHandler(data: any): CommunityReactions {
  return CommunityReactions.fromAPI(data);
}

type RiveFileProp = {
  riveFile: RiveFile;
};

type Props = {
  shotItem: CommunityShotItem;
  disableReactionsTray: boolean;
};

type SimpleReactionProps = {
  artboard?: string;
  animations?: string;
};

function SimpleReaction({
  artboard = 'love',
  animations = 'idle',
}: SimpleReactionProps) {
  const { reactions } = useContext(RiveFilesContext);

  // @ts-ignore
  if (reactions === null || reactions?.buffer === undefined) {
    return <></>;
  }

  return (
    <_SimpleReaction
      riveFile={reactions}
      artboard={artboard}
      animations={animations}
    />
  );
}

function _SimpleReaction({
  riveFile,
  artboard,
  animations,
}: SimpleReactionProps & RiveFileProp) {
  const { RiveComponent } = useRive({
    riveFile,
    artboard,
    animations,
    autoplay: true,
  });

  return <RiveComponent />;
}

const ReactionButton = ({ shotItem, disableReactionsTray = false }: Props) => {
  const [displayTray, setDisplayTray] = useState<boolean>(false);
  const [displayModal, setDisplayModal] = useState<boolean>(false);
  const [displaySelection, setDisplaySelection] = useState<string | null>(null);

  /**
   * The following state could all be pulled from the `reactions` object when it
   * loads from the server , however we track it seperately client side to
   * keep the UI feeling fast. This adds a bit of complexity to the component,
   * so keep things simple, we should only update this state in three places:
   *    - When we add/update a reaction in `addReaction`
   *    - When we remove a reaction in `removeReaction`
   *    - When we load/refresh our reactions object. This then overrides any
   *      client state with server state, however if we have been tracking
   *      correctly, this should be the same.
   */
  const [count, setCount] = useState<number>(0);
  const [sortedReactions, setSortedReactions] = useState<string[]>([]);
  const [reactedToName, setReactedToName] = useState<string | null>(null);
  // Use a ref for hasUserReacted since our touch handlers wrap things in closure.
  const hasUserReacted = useRef<boolean>(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { user } = useContext(UserContext);
  const signedIn = user?.signedIn ?? false;

  const router = useRouter();

  let timerId: any;
  const ref = useRef<HTMLDivElement>(null);

  // Get reactions for community post
  const { communityPostId, fileId } = shotItem;
  const { data: reactions } = useSWR(
    `/api/community-posts/${communityPostId}/reactions`,
    fetcher(reactionsHandler)
  );

  function debounce(callback: VoidCallback, time = 500) {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(callback, time);
  }

  function addReaction({
    name,
    isTray = true,
    isUpdate = false,
  }: {
    name: string;
    isTray: boolean;
    isUpdate: boolean;
  }) {
    if (reactions === undefined) {
      return;
    }

    if (!isUpdate) {
      setCount((current) => current + 1);
    }

    hasUserReacted.current = true;
    setDisplaySelection(name);
    setReactedToName(name);

    const { reactionId } = reactions.getByName(name);

    debounce(
      () => {
        updateCommunityReaction(communityPostId, reactionId);
        if (isUpdate) {
          logRemoveReactionEvent({ isTray });
        }
        analytics.addCommunityReaction(name, communityPostId, fileId, isTray);
      },
      // Perform tray updates immediately. Since the tray take time to disappear and load,
      // it removes the chances of weird updates happening if the user moves too quickly
      isTray ? 0 : 500
    );
  }

  function removeReaction({ isTray = true } = {}) {
    // Update state to give the appearance of a snappy UI.
    hasUserReacted.current = false;
    setDisplayTray(false);
    setDisplaySelection(null);
    setReactedToName(null);
    setCount((current) => current - 1);

    debounce(() => {
      removeCommunityReaction(communityPostId);
      logRemoveReactionEvent({ isTray });
    });
  }

  function logRemoveReactionEvent({ isTray = true } = {}) {
    const reactedToReaction = reactions?.getReactedToReaction();
    if (reactedToReaction !== undefined) {
      analytics.removeCommunityReaction(
        reactedToReaction.name,
        communityPostId,
        fileId,
        isTray
      );
    }
  }

  function showTray() {
    if (!disableReactionsTray) {
      setDisplayTray(true);
    }
  }

  function hideTray() {
    setDisplayTray(false);
  }

  function showModal() {
    setDisplayModal(true);
  }

  function hideModal() {
    setDisplayModal(false);
  }

  function onCountBreakdown(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    e.preventDefault();
    showModal();
  }

  function onTraySelect({ name }: { name: string }) {
    // If we haven't loaded the reactions from the server yet, do nothing.
    if (reactions === undefined) {
      return;
    }

    // If the user is not signed in, push to the login screen.
    if (!signedIn) {
      router.push('/login');
      return;
    }

    setDisplayTray(false);
    // If the user is clicking their currently selected reaction, then remove
    // their reaction from this post
    if (name === reactedToName) {
      removeReaction({ isTray: true });
    } else {
      addReaction({
        name: name,
        isTray: true,
        isUpdate: hasUserReacted.current,
      });
    }
  }

  function onReactionButtonClick() {
    // If we haven't loaded the reactions from the server yet, do nothing.
    if (reactions === undefined) {
      return;
    }

    // If the user is not logged in
    if (!signedIn) {
      router.push('/login');
      return;
    }

    if (hasUserReacted.current) {
      removeReaction({ isTray: false });
    } else {
      addReaction({ name: 'love', isTray: false, isUpdate: false });
    }
  }

  function handleTouchStart(e: Event) {
    e.preventDefault();
    timerId = setTimeout(() => {
      showTray();
      timerId = null;
    }, 500);
  }

  function handleTouchEnd(e: Event) {
    e.preventDefault();
    if (timerId != null) {
      onReactionButtonClick();
    }
    clearTimeout(timerId);
    timerId = null;
  }

  // These listeners handle touch interactions
  // in order to display the reaction tray on hold.
  //
  // Added this way in order to use false passive option
  // to ensure preventDefault works to stop mouse
  // interactions from triggering.
  //
  // They need to be added and removed on state change
  // to keep them in sync with the state.
  //
  // Basically, they're a source of pain, but I haven't
  // found any other way to make sure the differences in
  // touch and mouse behaviors don't conflict with each other.

  useEffect(() => {
    if (reactions) {
      setCount(reactions.getTotalCount());
      setSortedReactions(reactions.getSortedNames());
      const reactedTo = reactions.getReactedToReaction();
      setReactedToName(reactedTo?.name ?? null);
      hasUserReacted.current = reactions.hasUserReacted();
    }
  }, [reactions]);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener('touchstart', handleTouchStart, {
        passive: false,
      });
      ref.current.addEventListener('touchend', handleTouchEnd, {
        passive: false,
      });
    }
    return () => {
      if (ref.current) {
        ref.current.removeEventListener('touchstart', handleTouchStart);
        ref.current.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [reactions]);

  useEffect(() => {
    displayTray
      ? window.addEventListener('touchstart', close, true)
      : window.removeEventListener('touchstart', close, true);

    return () => {
      window.removeEventListener('touchstart', close, true);
    };
  }, [displayTray]);

  function close(e: Event) {
    if (displayTray && !ref.current?.contains(e.target as Node)) {
      hideTray();
    }
  }

  return (
    <>
      <div className={styles.ReactionButtonContainer}>
        <div
          ref={ref}
          className={styles.ReactionButton}
          onMouseEnter={showTray}
          onMouseMove={showTray}
          onMouseLeave={hideTray}
          onClick={onReactionButtonClick}
        >
          Like
          <div className={styles.UserReactions}>
            <div className={styles.SortedReactions}>
              {displaySelection ? (
                <div className={styles.InlineReaction}>
                  <SelectedReaction
                    reaction={displaySelection}
                    onStop={() => setDisplaySelection(null)}
                  />
                </div>
              ) : (
                sortedReactions
                  .slice()
                  .reverse()
                  .map((r, i) => (
                    <div
                      key={r}
                      className={styles.InlineReaction}
                      style={{
                        transform: `translateX(${
                          20 * -(sortedReactions.length - 1 - i)
                        }px)`,
                      }}
                    >
                      <SimpleReaction artboard={r} animations="idle" />
                    </div>
                  ))
              )}
            </div>
            {count === 0 && (
              <div className={join([styles.InlineReaction, styles.Inset])}>
                <SimpleReaction />
              </div>
            )}
          </div>
        </div>
        {count > 0 && (
          <button
            className={styles.ReactionCount}
            onClick={onCountBreakdown}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div
              className={join([
                styles.CountHighlight,
                ...insertIf(hasUserReacted.current, styles.ReactedTo),
              ])}
            >
              {count}
            </div>
          </button>
        )}
      </div>
      {reactions && (
        <CSSTransition
          in={displayTray}
          timeout={450}
          classNames={{
            enter: styles['TrayTransition-enter'],
            exit: styles['TrayTransition-exit'],
            enterActive: styles['TrayTransition-enter-active'],
            exitActive: styles['TrayTransition-exit-active'],
          }}
          unmountOnExit
        >
          <ReactionTray
            onReact={(name) => onTraySelect({ name })}
            onMouseEnter={showTray}
            onMouseLeave={hideTray}
            reactions={reactions}
            reactedToName={reactedToName}
          />
        </CSSTransition>
      )}

      {displayModal && reactions && (
        <ReactionModal onClose={hideModal} reactions={reactions} />
      )}
    </>
  );
};

ReactionButton.defaultProps = {
  disableReactionsTray: false,
};

export default ReactionButton;

type ReactionTrayProps = {
  onReact: (reaction: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  reactions: CommunityReactions;
  reactedToName: string | null;
};

const ReactionTray = ({
  onReact,
  onMouseEnter,
  onMouseLeave,
  reactions,
  reactedToName,
}: ReactionTrayProps) => {
  return (
    <div
      className={styles.ReactionTray}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {reactions.asOrderedList().map((r) => (
        <Reaction
          key={r.name}
          artboard={r.name}
          onClick={() => onReact(r.name.toLowerCase())}
          isSelected={r.name === reactedToName}
        />
      ))}
    </div>
  );
};

type ReactionProps = {
  artboard: string;
  onClick: () => void;
  disableHover?: boolean;
  isSelected?: boolean;
};

export const Reaction = (props: ReactionProps) => {
  const { reactions } = useContext(RiveFilesContext);

  // @ts-ignore
  if (reactions === null || reactions?.buffer === undefined) {
    return <></>;
  }

  return <_Reaction {...props} riveFile={reactions} />;
};

const _Reaction = ({
  riveFile,
  artboard,
  onClick,
  disableHover = false,
  isSelected = false,
}: ReactionProps & RiveFileProp) => {
  const { RiveComponent, rive } = useRive({
    riveFile,
    artboard: artboard,
    stateMachines: 'controller',
    autoplay: true,
  });
  const hoverInput = useStateMachineInput(rive, 'controller', 'isHover')!;
  return (
    <div className={styles.Reaction} onClick={onClick}>
      {isSelected && <div className={styles.SelectedReaction} />}
      <RiveComponent
        onMouseEnter={() => {
          if (hoverInput && !disableHover) hoverInput.value = true;
        }}
        onMouseLeave={() => {
          if (hoverInput && !disableHover) hoverInput.value = false;
        }}
      />
    </div>
  );
};

type SelectedReactionProps = {
  reaction: string;
  onStop: () => void;
};

const SelectedReaction = (props: SelectedReactionProps) => {
  const { reactions } = useContext(RiveFilesContext);

  // @ts-ignore
  if (reactions === null || reactions?.buffer === undefined) {
    return <></>;
  }

  return <_SelectedReaction {...props} riveFile={reactions} />;
};

const _SelectedReaction = ({
  riveFile,
  reaction,
  onStop,
}: SelectedReactionProps & { riveFile: RiveFile }) => {
  const { RiveComponent } = useRive({
    riveFile,
    artboard: reaction,
    animations: 'play',
    autoplay: true,
    onstop: onStop,
  });
  return <RiveComponent />;
};
